/* eslint max-lines: "off" */

import { createApi } from "@reduxjs/toolkit/query/react";
import {
  replaceTrackWithRelease,
  transformKeysToCamelCase,
} from "../utils/utils";
import { axiosBaseQuery } from "./api";
import { replaceCdnLinksForDev } from "../utils/replaceCdnLinksForDev";

export const podcastsApi = createApi({
  reducerPath: "podcastsApi",
  baseQuery: axiosBaseQuery,
  tagTypes: ["podcasts", "releases"],
  endpoints: (builder) => ({
    // Запросы для подкастов
    getPodcasts: builder.query({
      query: ({ link, id } = {}) => {
        const params = {};
        if (link) params.link = link;
        if (id) params.id = id;

        return {
          url: "podcasts",
          params,
          skipErrorToast: true,
        };
      },
      transformResponse: (response, _, args) => {
        const camelCaseData = transformKeysToCamelCase(response);
        const transformedData = replaceTrackWithRelease(camelCaseData).data;

        if (!transformedData) return [];

        if (transformedData && transformedData.podcastReleases?.length) {
          transformedData.podcastReleases = replaceCdnLinksForDev(
            transformedData.podcastReleases,
          );
        }

        if (args) {
          transformedData.avgRating = Number(transformedData.avgRating).toFixed(
            1,
          );
          return transformedData;
        }

        return transformedData
          .filter((podcast) => podcast.podcastReleases)
          .map((item) => {
            if (item.avgRating) {
              item.avgRating = Number(item.avgRating).toFixed(1);
            }
            return item;
          });
      },
      providesTags: (result) => {
        if (
          result &&
          result.podcastReleases &&
          Array.isArray(result.podcastReleases)
        ) {
          return [
            { type: "podcasts", id: "LIST" },
            ...result.podcastReleases.map((podcast) => ({
              type: "podcasts",
              id: podcast.id,
            })),
          ];
        }
        return [{ type: "podcasts", id: "LIST" }];
      },
    }),

    ratePodcast: builder.mutation({
      query: ({ podcastId, rating }) => ({
        url: "podcasts/rate",
        method: "POST",
        data: { podcast_id: podcastId, rating },
      }),
      invalidatesTags: ({ podcastId }) => [{ type: "podcasts", id: podcastId }],
    }),

    getFavoritePodcastsTracks: builder.infiniteQuery({
      providesTags: ["favoritePodcastsTracks"],
      infiniteQueryOptions: {
        initialPageParam: {
          offset: 0,
          limit: 50,
        },
        getNextPageParam: (
          lastPage,
          _allPages,
          lastPageParam,
          _allPageParams,
        ) => {
          const nextOffset = lastPageParam.offset + lastPageParam.limit;

          if (lastPage.total < lastPageParam.limit) {
            return undefined;
          }

          return {
            ...lastPageParam,
            offset: nextOffset,
          };
        },
      },
      query: ({ pageParam: { offset } }) => ({
        url: `user/liked/podcasts/tracks/offset/${offset}`,
      }),
      transformResponse: (response) => {
        const camelCaseData = transformKeysToCamelCase(response);
        return replaceTrackWithRelease(camelCaseData).data;
      },
    }),

    likePodcastTrack: builder.mutation({
      query: (body) => ({
        url: "podcasts/tracks/rate",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { podcast_track_id }) => [
        { type: "podcasts", id: podcast_track_id },
        "releases",
      ],
      async onQueryStarted(
        { podcast_track_id, is_liked },
        { dispatch, queryFulfilled },
      ) {
        const patchResults = dispatch(
          podcastsApi.util.updateQueryData(
            "getFavoritePodcastsTracks",
            undefined,
            (draft) => {
              for (const page of draft.pages) {
                const releaseIndex = page.podcastReleases.findIndex(
                  (release) => release.id === podcast_track_id,
                );

                if (releaseIndex !== -1) {
                  page.podcastReleases[releaseIndex] = {
                    ...page.podcastReleases[releaseIndex],
                    isLiked: is_liked,
                  };
                }
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchResults.undo();
        }
      },
    }),

    // Запросы для выпусков
    getReleases: builder.query({
      query: (type) => ({
        url: "podcasts/tracks",
        params: { type },
      }),
      transformResponse: (response) => {
        const camelCaseData = transformKeysToCamelCase(response);
        const transformedData = replaceTrackWithRelease(camelCaseData).data;

        if (transformedData && transformedData.latestPodcastReleases?.length) {
          transformedData.latestPodcastReleases = replaceCdnLinksForDev(
            transformedData.latestPodcastReleases,
          );
        }

        return transformedData;
      },
      providesTags: ["releases"],
    }),
    getTextFile: builder.query({
      queryFn: async (url) => {
        try {
          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(
              `Ошибка при загрузке файла: ${response.statusText}`,
            );
          }

          const text = await response.text();
          return { data: text };
        } catch (error) {
          return { error: error };
        }
      },
    }),
    listeningDepth: builder.mutation({
      query: (body) => {
        return {
          url: "podcasts/track/listening_depth",
          method: "POST",
          data: body,
        };
      },
    }),
    getLastReleasePlaying: builder.query({
      query: () => ({
        url: "podcasts/tracks/duration",
      }),
      transformResponse: (response) => {
        const camelCaseData = transformKeysToCamelCase(response);

        return camelCaseData.data;
      },
    }),
  }),
});
export const {
  useGetPodcastsQuery,
  useRatePodcastMutation,
  useLikePodcastTrackMutation,
  useGetReleasesQuery,
  useGetFavoritePodcastsTracksInfiniteQuery,
  useGetTextFileQuery,
  useListeningDepthMutation,
  useGetLastReleasePlayingQuery,
} = podcastsApi;
